import { PharmaciesSort } from '@/api/api.schemas';
import { Button } from '@/components/button';
import { DropdownInput } from '@/components/inputs/dropdown-input';
import { RadioGroup } from '@/components/inputs/radio-group';
import { Sheet } from '@/components/containers/sheet';
import { useTranslation } from '@/contexts/i18n-context';
import { useMediaQuery } from '@/hooks/use-media-query';
import clsx from 'clsx';
import { useState } from 'react';
import styles from './pharmacy-sort.module.css';

const translationBase = 'visit.pharmacySelection';

interface PharmacySortProps {
  sort: PharmaciesSort;
  isMailOrder: boolean;
  handleSortChange: (sort: PharmaciesSort) => void;
}

export function PharmacySort({ sort, isMailOrder, handleSortChange }: PharmacySortProps) {
  const { t } = useTranslation();
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);

  const isMobile = useMediaQuery('--screen-md');

  if (isMobile) {
    return (
      <>
        <Sheet
          isOpen={isSortSheetOpen}
          onClose={() => setIsSortSheetOpen(false)}
          title={t(`${translationBase}.sort.sheetTitle`)}
        >
          <SortRadioGroup
            sort={sort}
            isMailOrder={isMailOrder}
            handleSortChange={handleSortChange}
          />
        </Sheet>
        <Button
          onClick={() => setIsSortSheetOpen(true)}
          variant="filled"
          leftIcon="swap_vert"
          size="M"
          className={styles.sortButton}
        >
          {t(`${translationBase}.sort.sheetTitle`)}
        </Button>
      </>
    );
  }

  return (
    <div className={clsx(styles.sortOptions, 'body-3')}>
      <span>{t(`${translationBase}.sort.title`)}</span>
      <DropdownInput
        text={t(`${translationBase}.sort.options.${sort}`)}
        position="right"
        className={styles.sortDropdown}
      >
        <SortRadioGroup sort={sort} isMailOrder={isMailOrder} handleSortChange={handleSortChange} />
      </DropdownInput>
    </div>
  );
}

interface SortRadioGroup {
  sort: PharmaciesSort;
  isMailOrder: boolean;
  handleSortChange: (val: PharmaciesSort) => void;
}

function SortRadioGroup({ sort, isMailOrder, handleSortChange }: SortRadioGroup) {
  const { t } = useTranslation();
  let pharmaciesSorts = Object.values(PharmaciesSort);

  if (isMailOrder) {
    pharmaciesSorts = pharmaciesSorts.filter((sort) => sort !== PharmaciesSort.Distance);
  }

  return (
    <RadioGroup
      variant="dropdown"
      name="sort"
      label={t(`${translationBase}.sort.title`)}
      readersOnlyLabel
      className={styles.options}
      defaultCheckedValue={sort}
      onChange={(e) => handleSortChange(e.target.value as PharmaciesSort)}
      options={pharmaciesSorts.map((sort) => ({
        value: sort,
        label: t(`${translationBase}.sort.options.${sort}`),
      }))}
    />
  );
}
